import { encodeCanonicalGs1Value } from './check-digit.js';
import { QUALIFIER_ORDER_BY_PRIMARY } from './keys.js';
import { parseGS1DigitalLink } from './parse.js';
const LOOPBACK_HOST_REGEX = /^(?:localhost|127\.0\.0\.1)(?::\d+)?$/i;
function resolveGs1UrlSchemeAndHost(rawHost) {
    const trimmed = rawHost.trim();
    const hasExplicitHttp = /^http:\/\//i.test(trimmed);
    const hasExplicitHttps = /^https:\/\//i.test(trimmed);
    const cleanHost = trimmed
        .replace(/^https?:\/\//i, '')
        .replace(/\/+$/, '')
        .toLowerCase();
    const scheme = hasExplicitHttp
        ? 'http'
        : hasExplicitHttps
            ? 'https'
            : LOOPBACK_HOST_REGEX.test(cleanHost)
                ? 'http'
                : 'https';
    return { scheme, cleanHost };
}
/**
 * Builds the normalised host URI (`http(s)://{host}/{ai}/{canonicalPrimaryKey}{canonicalQualPath}`)
 * used in RFC 9264 `Link` headers and linkset anchors (Spec v2.1 §2.2 & §7.1 Stage 3).
 *
 * Clause: GS1 Digital Link Standard: URI Syntax v1.7.0 §4.11, pp35–37.
 */
export function buildNormalisedHostUri(host, dl) {
    const { scheme, cleanHost } = resolveGs1UrlSchemeAndHost(host);
    const allowedOrder = QUALIFIER_ORDER_BY_PRIMARY[dl.ai];
    const canonicalQualPath = allowedOrder
        .filter((qAi) => dl.qualifiers[qAi] !== undefined)
        .map((qAi) => `/${qAi}/${encodeCanonicalGs1Value(dl.qualifiers[qAi] ?? '')}`)
        .join('');
    return `${scheme}://${cleanHost}/${dl.ai}/${encodeCanonicalGs1Value(dl.primaryKey)}${canonicalQualPath}`;
}
/**
 * Validates and constructs a normalised GS1 Digital Link URL (`http(s)://{host}/{ai}/{canonicalPrimaryKey}{canonicalQualPath}`)
 * using `@myplayer/gs1` (`parseGS1DigitalLink` + `buildNormalisedHostUri`), so callers never build GS1 Digital Link URLs by hand.
 *
 * Clause: GS1 Digital Link Standard: URI Syntax v1.7.0 §4.1–§4.11 & §6.1;
 * GS1 General Specifications Release 26.0 §2.1.13.1, p122.
 */
export function buildGs1DigitalLinkUrl(params) {
    const { scheme, cleanHost } = resolveGs1UrlSchemeAndHost(params.host);
    if (cleanHost.length === 0) {
        throw new Error('GS1 Digital Link host must be a non-empty hostname.');
    }
    const qualSegments = [];
    if (params.qualifiers !== undefined) {
        for (const [qAi, qVal] of Object.entries(params.qualifiers)) {
            if (qVal.length > 0) {
                qualSegments.push(`/${encodeURIComponent(qAi)}/${encodeCanonicalGs1Value(qVal)}`);
            }
        }
    }
    const candidateUri = `${scheme}://${cleanHost}/${encodeURIComponent(params.ai)}/${encodeCanonicalGs1Value(params.primaryKey)}${qualSegments.join('')}`;
    const parsed = parseGS1DigitalLink(candidateUri);
    if (!parsed.ok) {
        throw new Error(`${parsed.message} [${parsed.errorCode} — ${parsed.clauseRef}]`);
    }
    return buildNormalisedHostUri(`${scheme}://${cleanHost}`, parsed.value);
}
/**
 * Builds the anchor URI (`http(s)://{host}/{ai}/{canonicalPrimaryKey}{qualifierPath}`) for a specific qualifier level.
 */
export function buildQualifierAnchorUri(host, dl, qualifierPath) {
    const { scheme, cleanHost } = resolveGs1UrlSchemeAndHost(host);
    return `${scheme}://${cleanHost}/${dl.ai}/${encodeCanonicalGs1Value(dl.primaryKey)}${qualifierPath}`;
}
/**
 * Populates destination URL template variables and appends the incoming query string
 * per GS1-Conformant Resolver Standard v1.2.1 §2.12 and Spec v2.1 §7.1 Stage 8 / §9.
 */
export function buildDestinationUrl(hrefTemplate, dl, options) {
    const gtinVal = dl.ai === '01' ? dl.primaryKey : (dl.secondaryKeys['01'] ?? '');
    const lotVal = dl.qualifiers['10'] ?? (dl.ai === '00' ? (dl.dataAttributes['10'] ?? '') : '');
    const serVal = dl.qualifiers['21'] ?? '';
    const cpvVal = dl.qualifiers['22'] ?? '';
    const tpxVal = dl.qualifiers['235'] ?? '';
    const glnxVal = dl.qualifiers['254'] ?? '';
    const expVal = dl.dataAttributes['17'] ?? '';
    const glnVal = dl.ai === '414' ? dl.primaryKey : (dl.secondaryKeys['414'] ?? '');
    const itipVal = dl.ai === '8006' ? dl.primaryKey : '';
    const cpidVal = dl.ai === '8010' ? dl.primaryKey : '';
    const cpidSerVal = dl.qualifiers['8011'] ?? '';
    const gmnVal = dl.ai === '8013' ? dl.primaryKey : '';
    const langVal = options?.lang ?? '';
    const projectIdVal = options?.projectId ?? '';
    const storeIdVal = options?.storeId ?? glnxVal;
    const vars = {
        '{01}': gtinVal,
        '{gtin}': gtinVal,
        '{10}': lotVal,
        '{lotnumber}': lotVal,
        '{21}': serVal,
        '{serialnumber}': serVal,
        '{22}': cpvVal,
        '{cpv}': cpvVal,
        '{235}': tpxVal,
        '{tpx}': tpxVal,
        '{254}': glnxVal,
        '{glnx}': glnxVal,
        '{17}': expVal,
        '{expDate}': expVal,
        '{414}': glnVal,
        '{gln}': glnVal,
        '{8006}': itipVal,
        '{itip}': itipVal,
        '{8010}': cpidVal,
        '{cpid}': cpidVal,
        '{8011}': cpidSerVal,
        '{8013}': gmnVal,
        '{gmn}': gmnVal,
        '{lang}': langVal,
        '{projectId}': projectIdVal,
        '{storeId}': storeIdVal,
    };
    let url = hrefTemplate;
    for (const [token, val] of Object.entries(vars)) {
        url = url.replaceAll(token, encodeURIComponent(val));
    }
    const target = new URL(url);
    if (options?.projectId !== undefined && options.projectId.length > 0) {
        if (gtinVal.length > 0 && !target.searchParams.has('gtin')) {
            target.searchParams.set('gtin', gtinVal);
        }
        if (lotVal.length > 0 && !target.searchParams.has('lot')) {
            target.searchParams.set('lot', lotVal);
        }
        if (serVal.length > 0 && !target.searchParams.has('serial')) {
            target.searchParams.set('serial', serVal);
        }
        if (storeIdVal.length > 0 && !target.searchParams.has('storeId')) {
            target.searchParams.set('storeId', storeIdVal);
        }
    }
    if (options?.forwardQueryString !== false) {
        const qs = (options?.rawQueryString ?? '').replace(/^\?/, '');
        if (qs.length > 0) {
            target.search = target.search.length > 0 ? `${target.search}&${qs}` : `?${qs}`;
        }
    }
    return target.toString();
}
/**
 * Forwards incoming query parameters to a resolved target URL per GS1-Conformant Resolver Standard v1.2.1 §2.12.
 */
export function applyQueryStringForwarding(targetHref, incomingUri, forwardQueryString = true) {
    if (!forwardQueryString) {
        return targetHref;
    }
    const incomingUrl = new URL(incomingUri);
    if (incomingUrl.search.length <= 1) {
        return targetHref;
    }
    const targetUrl = new URL(targetHref);
    for (const [key, val] of incomingUrl.searchParams.entries()) {
        if (!targetUrl.searchParams.has(key)) {
            targetUrl.searchParams.append(key, val);
        }
    }
    return targetUrl.toString();
}
/**
 * Generates Cloud CDN path-prefix invalidation rules for a GS1 identification key:
 * - Canonical `/${ai}/${encodedKey}*`
 * - Unpadded 8-, 12-, and 13-digit GTIN path prefixes for AI 01 (`URI Syntax v1.7.0 §4.1, p20`)
 * - Percent-encoding hex case variants (`%2F` vs `%2f`) and alphanumeric case variants
 * - Custom path stem prefixes (wildcard stem + explicit `customStems`)
 * - Query-string secondary key patterns (`?${ai}=${key}*`, `&${ai}=${key}*`)
 * - Compressed EPC binary path prefixes (`/ex*`, `/eh*`)
 *
 * Clause: GS1 Digital Link Standard: URI Syntax v1.7.0 §4.1, §4.11, §6.1.2;
 * Spec v2.1 §3.2 & §10 (Event-Driven Firestore -> Cloud CDN Invalidation).
 */
export function buildCacheInvalidationPathsForKey(ai, primaryKey, options) {
    const encodedKey = encodeCanonicalGs1Value(primaryKey);
    const keyVariants = new Set([encodedKey]);
    if (ai === '01') {
        const stripped = primaryKey.replace(/^0+/, '');
        for (const len of [8, 12, 13, 14]) {
            if (stripped.length <= len) {
                keyVariants.add(stripped.padStart(len, '0'));
            }
        }
    }
    // Percent-encoding lowercase hex variant (e.g. %2f vs %2F) and unencoded safe variant
    const lowerPct = encodedKey.replace(/%[0-9A-F]{2}/g, (m) => m.toLowerCase());
    if (lowerPct !== encodedKey) {
        keyVariants.add(lowerPct);
    }
    if (primaryKey !== encodedKey && !primaryKey.includes('/') && !primaryKey.includes('?')) {
        keyVariants.add(primaryKey);
    }
    // Case variants for alphanumeric keys
    const upperKey = encodeCanonicalGs1Value(primaryKey.toUpperCase());
    const lowerKey = encodeCanonicalGs1Value(primaryKey.toLowerCase());
    if (upperKey !== encodedKey) {
        keyVariants.add(upperKey);
    }
    if (lowerKey !== encodedKey) {
        keyVariants.add(lowerKey);
    }
    const paths = new Set();
    for (const kv of keyVariants) {
        paths.add(`/${ai}/${kv}*`);
        paths.add(`/${ai}/${kv}?linkType=linkset*`);
        paths.add(`/*/${ai}/${kv}*`);
        paths.add(`/*?${ai}=${kv}*`);
        paths.add(`/*&${ai}=${kv}*`);
        if (options?.customStems !== undefined) {
            for (const rawStem of options.customStems) {
                const stem = rawStem.replace(/^\/+|\/+$/g, '');
                if (stem.length > 0) {
                    paths.add(`/${stem}/${ai}/${kv}*`);
                }
            }
        }
    }
    if (options?.includeCompressedPrefixes !== false) {
        paths.add('/ex*');
        paths.add('/eh*');
    }
    return [...paths];
}
//# sourceMappingURL=url.js.map